// Enhanced CalendarEvents.tsx - Updated renderEventIcon function
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Link as LinkIcon,
  ListAlt as ListAltIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Today as TodayIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
  VideocamOutlined as VideocamIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Popover,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getISOWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { sv } from "date-fns/locale";
import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { DndContext, DragEndEvent, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { useActiveUserWorkItems } from "../../hooks/useActiveUserWorkItems";
import { useGetCalendarEventsQuery, useUpdateCalendarEventMutation } from "../../store/ms-services/calendarApi";
import { useUpdateNextActionMutation } from "../../store/services/nextActionService";
import {
  getWorkItemIcon,
  getWorkItemTypeName,
} from "../../utils/workItemUtils";
import ActivityLink from "../activity-link/ActivityLink";
import CalendarCustomerSearchDialog from "../customer-search/CalendarCustomerSearchDialog";
import {
  CalendarItem,
  formatItemTimeRange,
  mapCalendarEvents,
  mapWorkItems,
  mergeRelatedItems,
} from "./calendarModel";
import { TimeSlot } from "./TimeDropZone";
import DayView from "./DayView";
import MonthView from "./MonthView";
import WeekView from "./WeekView";

// Formatera månad och år
const formatMonthYear = (date: Date): string => {
  return format(date, "LLLL yyyy", { locale: sv });
};

export default function CalendarView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [viewType, setViewType] = useState<"day" | "week" | "month">(
    isMobile ? "day" : "week"
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarItem | null>(null);
  const [showCalendarEvents, setShowCalendarEvents] = useState<boolean>(true);
  const [showWorkItems, setShowWorkItems] = useState<boolean>(true);
  const [showFullDay, setShowFullDay] = useState<boolean>(false);
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  const [customerSearchDialogOpen, setCustomerSearchDialogOpen] =
    useState(false);
  const [selectedEventForCustomerSearch, setSelectedEventForCustomerSearch] =
    useState<CalendarItem | null>(null);

  // Drag and drop state
  const [draggedEvent, setDraggedEvent] = useState<CalendarItem | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  // Optimistic time overrides: eventId -> { start, end }
  const [eventTimeOverrides, setEventTimeOverrides] = useState<
    Map<string, { start: Date; end: Date }>
  >(new Map());
  const [updateCalendarEvent] = useUpdateCalendarEventMutation();
  const [updateNextAction] = useUpdateNextActionMutation();

  // Require 8px movement before drag starts (so clicks still work)
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const sensors = useSensors(pointerSensor);

  const handleDragStart = useCallback((event: { active: { data: { current?: { event?: CalendarItem } } } }) => {
    const calendarItem = event.active.data.current?.event;
    if (calendarItem) {
      setDraggedEvent(calendarItem);
    }
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setDraggedEvent(null);

    const { active, over } = event;
    if (!over) return;

    const calendarItem = active.data.current?.event as CalendarItem | undefined;
    const dropSlot = over.data.current as TimeSlot | undefined;

    if (!calendarItem || !dropSlot) return;

    // Heldagshändelser schemaläggs inte om via kalendern (en PATCH skulle
    // dessutom göra om dem till tidsatta event i Outlook)
    if (calendarItem.isAllDay) return;

    // Calculate new start time from the drop zone
    const newStart = new Date(dropSlot.date);
    if (dropSlot.hour === -1) {
      // Month view: only change the date, keep original time
      newStart.setHours(
        calendarItem.start.getHours(),
        calendarItem.start.getMinutes(),
        0, 0
      );
    } else {
      newStart.setHours(dropSlot.hour, dropSlot.minute, 0, 0);
    }

    // Preserve event duration
    const durationMs = calendarItem.end.getTime() - calendarItem.start.getTime();
    const newEnd = new Date(newStart.getTime() + durationMs);

    // Don't update if time didn't change
    if (newStart.getTime() === calendarItem.start.getTime()) return;

    // Optimistically move the event in the UI immediately
    setEventTimeOverrides((prev) => {
      const next = new Map(prev);
      next.set(calendarItem.id, { start: newStart, end: newEnd });
      return next;
    });

    // Track results per API
    let calendarOk = true;
    let crmOk = true;
    let calendarUpdated = false;
    let crmUpdated = false;

    // Determine the calendar event ID to update (from merged/calendar, or from workItem's ExternalId)
    const calendarEventId =
      calendarItem.relatedItems?.calendarEvent?.id ||
      calendarItem.relatedItems?.workItem?.ExternalId;

    // Update calendar event (Graph API) if there's a linked calendar event
    if (calendarEventId) {
      calendarUpdated = true;
      try {
        await updateCalendarEvent({
          eventId: calendarEventId,
          eventData: {
            start: {
              dateTime: newStart.toISOString(),
              timeZone: "UTC",
            },
            end: {
              dateTime: newEnd.toISOString(),
              timeZone: "UTC",
            },
          },
        }).unwrap();
      } catch (err) {
        console.error("Failed to update calendar event:", err);
        calendarOk = false;
      }
    }

    // Update work item (CRM API) - send local time (backend expects local, not UTC)
    if ((calendarItem.type === "workItem" || calendarItem.type === "merged") &&
        calendarItem.relatedItems?.workItem) {
      const workItem = calendarItem.relatedItems.workItem;
      const customerNo = workItem.Customer?.Id;
      if (customerNo) {
        crmUpdated = true;
        const localDateStr = format(newStart, "yyyy-MM-dd'T'HH:mm:ss");
        try {
          await updateNextAction({
            customerNo,
            request: {
              title: workItem.Name || "",
              scheduledDate: localDateStr,
              workItemNo: workItem.Id,
              type: workItem.Type,
              teamId: workItem.Team?.Id,
              assignedToId: workItem.AssignedTo?.Id,
              workNo: workItem.WorkId ?? undefined,
              externalId: workItem.ExternalId,
            },
          }).unwrap();
        } catch (err) {
          console.error("Failed to update work item:", err);
          crmOk = false;
        }
      }
    }

    // Show feedback based on results
    if (calendarOk && crmOk) {
      setSnackbarMessage(
        `"${calendarItem.title}" flyttad till ${format(newStart, "EEEE d MMMM, HH:mm", { locale: sv })}`
      );
    } else if (!calendarOk && !crmOk) {
      setSnackbarMessage("Kunde inte flytta händelsen. Försök igen.");
      // Both failed — revert optimistic update
      setEventTimeOverrides((prev) => {
        const next = new Map(prev);
        next.delete(calendarItem.id);
        return next;
      });
    } else if (!calendarOk) {
      setSnackbarMessage("Aktiviteten flyttades men kalendern i Outlook kunde inte uppdateras.");
    } else {
      setSnackbarMessage("Kalendern uppdaterades men aktiviteten kunde inte flyttas i CRM.");
    }
  }, [updateCalendarEvent, updateNextAction]);

  // Hover state management
  const [isHoveringEvent, setIsHoveringEvent] = useState(false);
  const [isHoveringPopover, setIsHoveringPopover] = useState(false);
  const [isHoverMode, setIsHoverMode] = useState(false); // Track if popover was opened by hover
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const dateRange = useMemo(() => {
    let startDate: Date;
    let endDate: Date;

    switch (viewType) {
      case "day":
        startDate = startOfDay(viewDate);
        endDate = endOfDay(viewDate);
        break;
      case "week":
        startDate = startOfWeek(viewDate, { weekStartsOn: 1 });
        endDate = endOfWeek(viewDate, { weekStartsOn: 1 });
        break;
      case "month":
        startDate = startOfMonth(viewDate);
        endDate = endOfMonth(viewDate);
        startDate = startOfWeek(startDate, { weekStartsOn: 1 });
        endDate = endOfWeek(endDate, { weekStartsOn: 1 });
        break;
      default:
        startDate = startOfDay(viewDate);
        endDate = addDays(startDate, 7);
        break;
    }

    return {
      startDateTime: startDate.toISOString(),
      endDateTime: endDate.toISOString(),
    };
  }, [viewDate, viewType]);

  const {
    data: userWorkItems = [],
    isLoading: isWorksLoading,
    error: worksError,
  } = useActiveUserWorkItems({
    fromDate: dateRange.startDateTime,
    toDate: dateRange.endDateTime,
  });

  const {
    data: calendarEvents = [],
    isLoading: loading,
    error,
  } = useGetCalendarEventsQuery(dateRange);

  const allEvents = useMemo(() => {
    const combinedEvents: CalendarItem[] = [];
    const calendarItems = showCalendarEvents
      ? mapCalendarEvents(calendarEvents)
      : [];
    const workItems = showWorkItems ? mapWorkItems(userWorkItems) : [];

    let events: CalendarItem[];
    if (showCalendarEvents && showWorkItems && userWorkItems) {
      events = mergeRelatedItems(calendarItems, workItems);
    } else {
      if (showCalendarEvents) {
        combinedEvents.push(...calendarItems);
      }
      if (showWorkItems && userWorkItems) {
        combinedEvents.push(...workItems);
      }
      events = combinedEvents;
    }

    // Apply optimistic time overrides from drag-and-drop
    if (eventTimeOverrides.size > 0) {
      events = events.map((event) => {
        const override = eventTimeOverrides.get(event.id);
        if (override) {
          return { ...event, start: override.start, end: override.end };
        }
        return event;
      });
    }

    return events;
  }, [calendarEvents, userWorkItems, showCalendarEvents, showWorkItems, eventTimeOverrides]);

  // Clear optimistic overrides when API data refreshes (new data from server)
  useEffect(() => {
    if (eventTimeOverrides.size > 0) {
      setEventTimeOverrides(new Map());
    }
    // Only trigger when server data changes, not when overrides change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarEvents, userWorkItems]);

  const handleViewChange = (
    event: React.SyntheticEvent,
    newValue: "day" | "week" | "month"
  ) => {
    setViewType(newValue);
  };

  const handleDateChange = (amount: number) => {
    const newDate = new Date(viewDate);
    if (viewType === "day") {
      newDate.setDate(newDate.getDate() + amount);
    } else if (viewType === "week") {
      newDate.setDate(newDate.getDate() + amount * 7);
    } else if (viewType === "month") {
      newDate.setMonth(newDate.getMonth() + amount);
    }
    setViewDate(newDate);
  };

  const handleToday = () => {
    setViewDate(new Date());
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const handleEventClick = (
    event: React.MouseEvent<HTMLElement>,
    calendarItem: CalendarItem
  ) => {
    console.log(calendarItem);

    // Clear any pending timeouts since we're showing explicitly
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    setIsHoverMode(false); // This was opened by click, not hover
    setAnchorEl(event.currentTarget);
    setSelectedEvent(calendarItem);
  };

  const handleEventHover = (
    event: React.MouseEvent<HTMLElement>,
    calendarItem: CalendarItem
  ) => {
    console.log("Hover triggered!", calendarItem.title); // Debug log

    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    setIsHoveringEvent(true);

    // Get position for hover modal
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverPosition({
      x: rect.left,
      y: rect.bottom + 8
    });

    // Show immediately for hover
    setIsHoverMode(true);
    setSelectedEvent(calendarItem);
  };

  const handleEventLeave = () => {
    console.log("Leave triggered!"); // Debug log

    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    setIsHoveringEvent(false);

    // Hide hover popover with a delay
    hideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringPopover && isHoverMode) {
        setIsHoverMode(false);
        setSelectedEvent(null);
        setHoverPosition(null);
      }
    }, 150);
  };

  const handlePopoverEnter = () => {
    // Clear any pending hide timeout when entering popover
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHoveringPopover(true);
  };

  const handlePopoverLeave = () => {
    setIsHoveringPopover(false);

    // Hide popover with delay if not hovering event
    hideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringEvent) {
        setAnchorEl(null);
        setSelectedEvent(null);
      }
    }, 150);
  };

  const handleLinkToCustomer = () => {
    if (selectedEvent) {
      setSelectedEventForCustomerSearch(selectedEvent);
      setCustomerSearchDialogOpen(true);
      handlePopoverClose(); // Stäng popover
    }
  };

  const handlePopoverClose = (event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
    console.log("Popover close triggered:", reason); // Debug log

    // Clear any pending timeouts
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Reset all states
    setIsHoveringEvent(false);
    setIsHoveringPopover(false);
    setIsHoverMode(false);

    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const handleToggleCalendarEvents = () => {
    setShowCalendarEvents(!showCalendarEvents);
  };

  const handleToggleWorkItems = () => {
    setShowWorkItems(!showWorkItems);
  };

  const shouldShowCustomerLinkButton = (event: CalendarItem): boolean => {
    // Visa bara för calendar events som inte är merged
    return event.type === "calendar" && !event.relatedItems?.workItem;
  };

  if (loading || isWorksLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || worksError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {worksError
          ? "Kunde inte ladda arbetsuppgifter"
          : "Kunde inte ladda besök"}
      </Alert>
    );
  }

  // Enhanced renderEventIcon function with better icon selection
  const renderEventIcon = (event: CalendarItem) => {
    // For work items and merged items, use the work item type icon
    if (
      (event.type === "workItem" || event.type === "merged") &&
      event.workItemType
    ) {
      return getWorkItemIcon(event.workItemType);
    }

    // For calendar events, check if it's an online meeting first
    if (event.type === "calendar") {
      const calendarData = event.relatedItems?.calendarEvent;
      if (calendarData && calendarData.isOnlineMeeting) {
        return <VideocamIcon fontSize="small" />;
      }
      // Default calendar icon
      return <TodayIcon fontSize="small" />;
    }

    // For merged events, check both calendar and work item data
    if (event.type === "merged" && event.relatedItems?.calendarEvent) {
      if (event.relatedItems.calendarEvent.isOnlineMeeting) {
        return <VideocamIcon fontSize="small" />;
      }
    }

    // For visit type
    if (event.type === "visit") {
      return <ListAltIcon fontSize="small" />;
    }

    // Default fallback
    return <WorkIcon fontSize="small" />;
  };

  return (
    <Box>
      {/* Calendar navigation and toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          rowGap: 1,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => handleDateChange(-1)}>
            <ChevronLeftIcon />
          </IconButton>

          <Tooltip title="Hoppa till datum">
            <Box
              onClick={(e) => setDatePickerAnchor(e.currentTarget)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 0,
                cursor: "pointer",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="h6" sx={{ textAlign: "center", minWidth: 0 }}>
                {viewType === "day"
                  ? format(viewDate, "EEEE d MMMM", { locale: sv })
                  : formatMonthYear(viewDate)}
              </Typography>
              {viewType === "week" && (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                  v. {getISOWeek(viewDate)}
                </Typography>
              )}
            </Box>
          </Tooltip>

          <IconButton onClick={() => handleDateChange(1)}>
            <ChevronRightIcon />
          </IconButton>

          <IconButton onClick={handleToday}>
            <TodayIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", rowGap: 1 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "row", sm: "column" }, flexWrap: "wrap", columnGap: 1, mr: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCalendarEvents}
                  onChange={handleToggleCalendarEvents}
                  size="small"
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TodayIcon
                    fontSize="small"
                    sx={{ mr: 0.5, color: "#2196f3" }}
                  />
                  Kalender
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showWorkItems}
                  onChange={handleToggleWorkItems}
                  size="small"
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <WorkIcon
                    fontSize="small"
                    sx={{ mr: 0.5, color: "#ff9800" }}
                  />
                  Arbetsuppgifter
                </Typography>
              }
            />
          </Box>

          <Tabs value={viewType} onChange={handleViewChange}>
            <Tab label="Dag" value="day" />
            <Tab label="Vecka" value="week" />
            <Tab label="Månad" value="month" />
          </Tabs>

          {viewType !== "month" && (
            <Tooltip title={showFullDay ? "Visa arbetstid" : "Visa hela dagen"}>
              <IconButton
                size="small"
                onClick={() => setShowFullDay((prev) => !prev)}
              >
                {showFullDay ? (
                  <UnfoldLessIcon fontSize="small" />
                ) : (
                  <UnfoldMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Calendar view */}
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Card>
          {viewType === "day" && (
            <DayView
              viewDate={viewDate}
              events={allEvents}
              onEventClick={handleEventClick}
              onEventHover={handleEventHover}
              onEventLeave={handleEventLeave}
              renderEventIcon={renderEventIcon}
              showFullDay={showFullDay}
            />
          )}
          {viewType === "week" && (
            <WeekView
              viewDate={viewDate}
              events={allEvents}
              onEventClick={handleEventClick}
              onEventHover={handleEventHover}
              onEventLeave={handleEventLeave}
              renderEventIcon={renderEventIcon}
              showFullDay={showFullDay}
            />
          )}
          {viewType === "month" && (
            <MonthView
              viewDate={viewDate}
              events={allEvents}
              onEventClick={handleEventClick}
              onEventHover={handleEventHover}
              onEventLeave={handleEventLeave}
              renderEventIcon={renderEventIcon}
            />
          )}
        </Card>

        {/* Drag overlay - ghost of the dragged event */}
        <DragOverlay>
          {draggedEvent && (
            <Box
              sx={{
                p: 1,
                bgcolor: draggedEvent.color || "primary.light",
                color: "white",
                borderRadius: 1,
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                opacity: 0.85,
                maxWidth: 200,
                pointerEvents: "none",
              }}
            >
              <Typography variant="caption" fontWeight="bold" noWrap>
                {draggedEvent.title}
              </Typography>
              <Typography variant="caption" display="block" sx={{ fontSize: "10px" }}>
                {format(draggedEvent.start, "HH:mm")} - {format(draggedEvent.end, "HH:mm")}
              </Typography>
            </Box>
          )}
        </DragOverlay>
      </DndContext>

      {/* Event info popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{
          pointerEvents: "auto",
        }}
        PaperProps={{
          onMouseEnter: handlePopoverEnter,
          onMouseLeave: handlePopoverLeave,
          sx: {
            pointerEvents: "auto",
          },
        }}
      >
        {selectedEvent && (
          <Box sx={{ p: 2, width: 350, maxWidth: "100%" }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: selectedEvent.color,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {renderEventIcon(selectedEvent)}
              {selectedEvent.title}
            </Typography>

            {/* Type information for WorkItems */}
            {(selectedEvent.type === "workItem" ||
              selectedEvent.type === "merged") &&
              selectedEvent.workItemType && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <WorkIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Typ: {getWorkItemTypeName(selectedEvent.workItemType)}
                  </Typography>
                </Box>
              )}

            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {formatItemTimeRange(selectedEvent)}
              </Typography>
            </Box>

            {/* Location */}
            {selectedEvent.location && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  📍 {selectedEvent.location}
                </Typography>
              </Box>
            )}

            {/* Organizer info */}
            {selectedEvent.type === "calendar" && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {
                    selectedEvent.relatedItems?.calendarEvent?.organizer
                      .emailAddress.name
                  }
                </Typography>
              </Box>
            )}

            {/* Online meeting link */}
            {selectedEvent.type === "calendar" && (
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <VideocamIcon fontSize="small" color="action" />
                <Button
                  size="small"
                  variant="outlined"
                  href={
                    selectedEvent.relatedItems?.calendarEvent?.onlineMeeting
                      ?.joinUrl ?? ""
                  }
                  target="_blank"
                  startIcon={<LinkIcon />}
                >
                  Gå med i möte
                </Button>
              </Box>
            )}

            {/* Customer linking button for calendar events without workItem */}
            {shouldShowCustomerLinkButton(selectedEvent) && (
              <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<PersonAddIcon />}
                  onClick={handleLinkToCustomer}
                >
                  Koppla till kund
                </Button>
                <Typography
                  variant="caption"
                  display="block"
                  textAlign="center"
                  mt={1}
                  color="text.secondary"
                >
                  Skapa en aktivitet kopplad till denna kalenderhändelse
                </Typography>
              </Box>
            )}

            {/* Show work item info if it's a merged event */}
            {selectedEvent.type !== "calendar" &&
              selectedEvent.relatedItems?.workItem && (
                <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                  <Typography
                    variant="body2"
                    color="primary"
                    fontWeight="medium"
                  >
                    Kopplad till aktivitet
                  </Typography>
                  <ActivityLink
                    style={{ textDecoration: "none" }}
                    workItemNo={selectedEvent.relatedItems.workItem.Id}
                    customerNo={
                      selectedEvent.relatedItems.workItem.Customer?.Id
                    }
                    to={`/activity/${selectedEvent.relatedItems.workItem.Id}`}
                    key={selectedEvent.relatedItems.workItem.Id}
                  >
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={getWorkItemIcon(
                        selectedEvent.relatedItems.workItem.Type
                      )}
                      sx={{ mt: 1 }}
                      color="primary"
                    >
                      Öppna aktivitet
                    </Button>
                  </ActivityLink>
                </Box>
              )}
          </Box>
        )}
      </Popover>

      {/* Hover Modal */}
      {isHoverMode && selectedEvent && hoverPosition && (
        <Box
          sx={{
            position: "fixed",
            left: hoverPosition.x,
            top: hoverPosition.y,
            zIndex: 9999,
            pointerEvents: "auto",
          }}
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
        >
          <Box sx={{
            p: 2,
            width: 350,
            maxWidth: "100%",
            bgcolor: "background.paper",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider"
          }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: selectedEvent.color,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {renderEventIcon(selectedEvent)}
              {selectedEvent.title}
            </Typography>

            {/* Type information for WorkItems */}
            {(selectedEvent.type === "workItem" ||
              selectedEvent.type === "merged") &&
              selectedEvent.workItemType && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <WorkIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Typ: {getWorkItemTypeName(selectedEvent.workItemType)}
                  </Typography>
                </Box>
              )}

            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {formatItemTimeRange(selectedEvent)}
              </Typography>
            </Box>

            {/* Location */}
            {selectedEvent.location && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  📍 {selectedEvent.location}
                </Typography>
              </Box>
            )}

            {/* Organizer info */}
            {selectedEvent.type === "calendar" && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {
                    selectedEvent.relatedItems?.calendarEvent?.organizer
                      .emailAddress.name
                  }
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {selectedEventForCustomerSearch && (
        <CalendarCustomerSearchDialog
          open={customerSearchDialogOpen}
          onClose={() => {
            setCustomerSearchDialogOpen(false);
            setSelectedEventForCustomerSearch(null);
          }}
          calendarEvent={selectedEventForCustomerSearch}
        />
      )}

      {/* Date picker popover */}
      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={() => setDatePickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <DateCalendar
          value={viewDate}
          onChange={(newDate) => {
            if (newDate) {
              setViewDate(newDate);
              setDatePickerAnchor(null);
            }
          }}
        />
      </Popover>

      {/* Snackbar for drag-and-drop feedback */}
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
